/**
 * PAGE: LISTE ÉQUIPAGES (VERSION REFACTORISÉE)
 * Note: Page complexe avec beaucoup d'édition inline - on garde la logique métier
 */

import type { ColumnDef } from '@tanstack/react-table';
import { Tag } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BulkAssignUserModal,
  BulkCreateTaskModal,
  ColumnSelectorModal,
  CreateCrewModal,
  FiltersModal,
  ImportCSVModal,
  SendBulkEmailModal,
} from '../components/modals';
import type { CreateCrewWithBpiData } from '../components/modals/CreateCrewModal';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { ActiveFilterChips, SavedSearchToolbar } from '../components/search';
import { TableCell } from '../components/table';
import { EnterpriseTable, SelectAllBanner } from '../components/tables';
import { ActionsCell, CheckboxCell, DateCell, EmptyCell } from '../components/tables/cells';
import { BulkActionsBar, ConfirmDialog, ErrorState, Select, toast } from '../components/ui';
import { Badge } from '../components/ui/badge';
import { CREW_CONFIG } from '../config/entities/crew.config';
import { useUsers } from '../contexts/UsersContext';
import { useAssociationsSelect } from '../hooks/useAssociationsSelect';
import { useCrewActions } from '../hooks/useCrewActions';
import { useCrews } from '../hooks/useCrews';
import { useDiscountsSelect } from '../hooks/useDiscountsSelect';
import { useEntityList } from '../hooks/useEntityList';
import { usePermissions } from '../hooks/usePermissions';
import { useProducts } from '../hooks/useProducts';
import { useProspects } from '../hooks/useProspects';
import { useSearchSessionMemory } from '../hooks/useSearchSessionMemory';
import { useServerPagination } from '../hooks/useServerPagination';
import { useSupabase } from '../hooks/useSupabase';
import { useVehiclesSelect } from '../hooks/useVehiclesSelect';
import {
  PROBABILITY_FILTER_OPTIONS,
  PROBABILITY_SELECT_OPTIONS,
} from '../lib/crew-probability-constants';
import {
  generateColumnOptions,
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateInitialColumnVisibility,
} from '../lib/entity-config';
import { isOver28AtEventDate } from '../lib/age-utils';
import { deleteCrewErrorMessage } from '../lib/crewDeleteErrors';
import { ROUTES } from '../lib/routes/constants';
import type { Crew, CrewProbability } from '../types/crew';
import { CREW_FINANCING_TYPE_LABELS, CREW_STATUS_COLORS, CREW_STATUS_LABELS } from '../types/crew';
import type { FilterCondition, FilterConfig } from '../types/filters';
import { createSavedSearchDefinition, type SavedSearchDefinition } from '../types/saved-search';

export function CrewsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const supabase = useSupabase();
  const {
    error,
    create,
    update,
    remove: _remove,
    removeMany,
    refresh,
    fetchPaginated,
    fetchFiltered,
    fetchAllFilteredIds,
    allData,
  } = useCrews({
    mode: 'server',
  });
  const { addCrewMember, deleteCrew } = useCrewActions();
  const { deleteProspectByContactAndProduct } = useProspects({ mode: 'client' });

  // Permissions utilisateur
  const {
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canManageAssignments,
  } = usePermissions();

  // Données pour les filtres dropdown
  const { products } = useProducts();
  const { vehicles } = useVehiclesSelect();
  const { associations } = useAssociationsSelect();
  const { discounts } = useDiscountsSelect();
  const { usersMap } = useUsers();

  // ✅ Lire les filtres depuis l'URL de manière synchrone (avant serverPagination)
  const initialUrlFiltersRef = useRef<FilterCondition[] | null>(null);
  const hasCleanedUrl = useRef(false);

  // Parser les filtres URL une seule fois au premier render
  if (initialUrlFiltersRef.current === null) {
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        const urlFilters = JSON.parse(filtersParam) as FilterCondition[];
        if (Array.isArray(urlFilters) && urlFilters.length > 0) {
          initialUrlFiltersRef.current = urlFilters;
        } else {
          initialUrlFiltersRef.current = [];
        }
      } catch (e) {
        console.error('[CrewsListPage] Erreur parsing filtres URL:', e);
        initialUrlFiltersRef.current = [];
      }
    } else {
      initialUrlFiltersRef.current = [];
    }
  }

  // Pagination server-side avec filtres initiaux depuis l'URL
  // Tri par défaut: numéro d'équipage décroissant (plus récents en premier)
  const serverPagination = useServerPagination<Crew>(fetchPaginated!, {
    initialLimit: 25,
    initialFilters: initialUrlFiltersRef.current || [],
    initialSort: { field: 'crew_number', direction: 'desc' },
  });
  const crews: Crew[] = serverPagination.data;
  const loading = serverPagination.loading;

  // ✅ Nettoyer l'URL après le montage (retirer les filtres de l'URL)
  useEffect(() => {
    if (
      !hasCleanedUrl.current &&
      initialUrlFiltersRef.current &&
      initialUrlFiltersRef.current.length > 0
    ) {
      hasCleanedUrl.current = true;
      // Retirer le paramètre de l'URL pour éviter de réappliquer les filtres au refresh
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('filters');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [isBulkEmailModalOpen, setIsBulkEmailModalOpen] = useState(false);
  const [selectedCrewsForEmail, setSelectedCrewsForEmail] = useState<Crew[]>([]);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [isBulkCreateTaskModalOpen, setIsBulkCreateTaskModalOpen] = useState(false);
  // État pour la sélection globale (Feature 4.4)
  const [isAllResultsSelected, setIsAllResultsSelected] = useState(false);
  const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);

  // Générer automatiquement depuis CREW_CONFIG
  const csvColumns = useMemo(() => generateCSVColumns(CREW_CONFIG), []);
  const csvTransformers = useMemo(() => generateCSVTransformers(CREW_CONFIG), []);
  const importConfig = useMemo(() => generateImportConfig(CREW_CONFIG), []);
  const initialColumnVisibility = useMemo(() => generateInitialColumnVisibility(CREW_CONFIG), []);

  // Filtres personnalisés car ils ont des options spécifiques
  const filterConfigs: FilterConfig[] = useMemo(() => {
    const baseFilters: FilterConfig[] = [
      {
        field: 'name',
        label: "Nom d'équipage",
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      {
        field: 'crew_number',
        label: "Numéro d'équipage",
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      {
        field: 'status',
        label: 'Statut',
        type: 'select' as const,
        options: Object.entries(CREW_STATUS_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
      },
      {
        field: 'probability',
        label: 'Probabilité',
        type: 'select' as const,
        options: PROBABILITY_FILTER_OPTIONS,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
      },
      {
        field: 'budget',
        label: 'Budget (€)',
        type: 'number' as const,
        operators: [
          'equals',
          'notEquals',
          'greaterThan',
          'lessThan',
          'between',
          'isNotNull',
          'isNull',
        ] as const,
      },
      {
        field: 'created_at',
        label: 'Date de création',
        type: 'date' as const,
        operators: [
          'equals',
          'notEquals',
          'sameDay',
          'before',
          'after',
          'between',
          'isNotNull',
          'isNull',
        ] as const,
      },
      {
        field: 'product_id',
        label: 'Produit',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: products.map((p) => ({ value: p.id, label: p.name })),
      },
      {
        field: 'vehicle_id',
        label: 'Véhicule',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: vehicles.map((v) => ({ value: v.id, label: v.registration })),
      },
      // V2 R4 Phase B : filtre déclaratif "possède un véhicule" (bool réel sur crews)
      {
        field: 'has_vehicle',
        label: 'Possède un véhicule',
        type: 'select' as const,
        operators: ['equals', 'notEquals'] as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
      },
      {
        field: 'association_id',
        label: 'Association',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: associations.map((a) => ({ value: a.id, label: a.name })),
      },
      {
        field: 'discount_id',
        label: 'Réduction',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: discounts.map((d) => ({ value: d.id, label: d.name })),
      },
      {
        field: 'financing_type',
        label: 'Type de financement',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: Object.entries(CREW_FINANCING_TYPE_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
      },
      {
        field: 'report',
        label: 'Motif annulation/report',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      {
        field: 'has_derogation',
        label: 'Dérogation validée',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
      },
      {
        field: 'derogation_pending',
        label: 'Dérogation en attente',
        type: 'select' as const,
        operators: ['equals', 'notEquals'] as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
      },
      {
        field: 'organizes_pvd',
        label: 'Organise PVD',
        type: 'select' as const,
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
      },
      {
        field: 'notes',
        label: 'Notes',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      {
        field: 'member_names',
        label: 'Membres',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      // Bug 2 fix-tests-empiriques (R7) : filtre nom complet pilote (vue crews_filter_view migration 00054)
      {
        field: 'pilot_full_name',
        label: 'Pilote - Nom',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      // R7 : filtre code postal pilote (vue crews_filter_view migration 00042)
      {
        field: 'pilot_postal_code',
        label: 'Pilote - Code postal',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      // R7 : filtre ville pilote (vue crews_filter_view migration 00042)
      {
        field: 'pilot_city',
        label: 'Pilote - Ville',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
      // R10 : filtre code BPI (vue crews_filter_view migration 00046)
      {
        field: 'bpi_code',
        label: 'Code BPI',
        type: 'text' as const,
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'isNotNull',
          'isNull',
        ] as const,
      },
    ];

    // Filtre "Assigné à" — toujours disponible (la restriction est faite par RLS sur les données)
    const userOptions = Object.values(usersMap).map((user) => ({
      value: user.id,
      label: user.full_name || user.email,
    }));
    baseFilters.push({
      field: 'assigned_to',
      label: 'Assigné à',
      type: 'select' as const,
      operators: ['equals', 'notEquals', 'isNotNull', 'isNull'] as const,
      options: userOptions,
    });

    // Filtre "Nombre d'appels" sur la colonne agrégée call_count (vue crews_filter_view)
    baseFilters.push({
      field: 'call_count',
      label: "Nombre d'appels",
      type: 'number' as const,
      operators: [
        'equals',
        'notEquals',
        'greaterThan',
        'lessThan',
        'between',
        'isNotNull',
        'isNull',
      ] as const,
    });

    return baseFilters;
  }, [products, vehicles, associations, discounts, usersMap]);

  const entity = useEntityList({
    data: crews || [],
    allData, // Toutes les données pour export/import
    loading,
    error,
    create,
    remove: deleteCrew,
    removeMany,
    removeErrorMessage: deleteCrewErrorMessage,
    refresh: async () => {
      const data = await refresh();
      await serverPagination.refresh();
      return data;
    },
    fetchFiltered, // ✅ Pour export CSV des données filtrées
    entityName: CREW_CONFIG.entityName,
    entityNamePlural: CREW_CONFIG.entityNamePlural,
    searchableFields: ['name', 'crew_number'] as (keyof Crew)[],
    filterConfigs,
    initialFilters: initialUrlFiltersRef.current || [], // ✅ Filtres depuis l'URL
    initialColumnVisibility, // ✅ Visibilité initiale des colonnes
    csvFileName: 'equipages.csv',
    csvColumns,
    csvTransformers,
    importConfig,

    // Mode server-side
    mode: 'server',
    total: serverPagination.total,
    totalPages: serverPagination.totalPages,
    currentPage: serverPagination.currentPage,
    itemsPerPage: serverPagination.itemsPerPage,
    onSearchChange: serverPagination.setSearchTerm,
    onPageChange: serverPagination.setCurrentPage,
    onItemsPerPageChange: serverPagination.setItemsPerPage,
    onFiltersChange: serverPagination.setFilters,
  });

  const { handleDeleteClick } = entity;

  const currentSavedSearchDefinition = useMemo(
    () =>
      createSavedSearchDefinition({
        searchTerm: entity.table.searchTerm,
        filters: entity.table.activeFilters,
        requiredFilters: [],
        filterMode: entity.table.filterMode,
        sortField: serverPagination.sortField,
        sortDirection: serverPagination.sortDirection,
        columnVisibility: entity.table.columnVisibility,
        pageSize: entity.table.itemsPerPage,
      }),
    [
      entity.table.activeFilters,
      entity.table.columnVisibility,
      entity.table.filterMode,
      entity.table.itemsPerPage,
      entity.table.searchTerm,
      serverPagination.sortDirection,
      serverPagination.sortField,
    ]
  );

  const defaultSavedSearchDefinition = useMemo(
    () =>
      createSavedSearchDefinition({
        searchTerm: '',
        filters: [],
        requiredFilters: [],
        filterMode: 'and',
        sortField: null,
        sortDirection: 'desc',
        columnVisibility: initialColumnVisibility,
        pageSize: 25,
      }),
    [initialColumnVisibility]
  );

  function applySavedSearchDefinition(definition: SavedSearchDefinition) {
    entity.table.setSearchTerm(definition.searchTerm);
    entity.table.setActiveFilters(definition.filters, definition.filterMode);
    entity.table.setColumnVisibility(definition.columnVisibility);
    entity.table.setItemsPerPage(definition.pageSize);
    serverPagination.setSort(definition.sort?.field || null, definition.sort?.direction);
    entity.table.clearSelection();
  }

  useSearchSessionMemory({
    entityType: 'crews',
    currentDefinition: currentSavedSearchDefinition,
    filterConfigs: entity.filterConfigs,
    onRestore: applySavedSearchDefinition,
    skip: searchParams.get('filters') !== null,
  });

  // Logique d'édition inline du budget
  const handleUpdateBudget = useCallback(
    async (id: string, newBudget: string) => {
      if (!update) return;
      const budgetValue = parseInt(newBudget, 10);
      if (isNaN(budgetValue)) {
        toast.error('Budget invalide');
        return;
      }
      const success = await update(id, { budget: budgetValue });
      if (success) {
        // Rafraîchir la liste pour récupérer les données mises à jour
        await serverPagination.refresh();
        toast.success('Budget mis à jour');
      } else {
        toast.error('Erreur lors de la mise à jour du budget');
      }
    },
    [update, serverPagination]
  );

  // Logique d'édition inline de la probabilité
  const handleUpdateProbability = useCallback(
    async (id: string, newProbability: string) => {
      if (!update) return;
      const success = await update(id, { probability: newProbability as CrewProbability });
      if (success) {
        // Rafraîchir la liste pour récupérer les données mises à jour
        await serverPagination.refresh();
        toast.success('Probabilité mise à jour');
      } else {
        toast.error('Erreur lors de la mise à jour de la probabilité');
      }
    },
    [update, serverPagination]
  );

  const handleBulkEmail = async () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun équipage sélectionné');
      return;
    }

    // ✅ En mode server-side pagination, récupérer les données par IDs depuis la base
    const selectedIds = Array.from(entity.table.selectedIds);

    try {
      const { data: selectedCrews, error: fetchError } = await supabase
        .from('crews')
        .select('*')
        .in('id', selectedIds);

      if (fetchError) {
        console.error('Error fetching crews for email:', fetchError);
        toast.error('Erreur lors de la récupération des équipages');
        return;
      }

      if (!selectedCrews || selectedCrews.length === 0) {
        toast.error('Impossible de récupérer les équipages sélectionnés');
        return;
      }

      setSelectedCrewsForEmail(selectedCrews as Crew[]);
      setIsBulkEmailModalOpen(true);
    } catch (err) {
      console.error('Error in handleBulkEmail:', err);
      toast.error("Erreur lors de la préparation de l'envoi");
    }
  };

  /**
   * Ouvrir la modal d'attribution en masse
   */
  const handleBulkAssign = () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun équipage sélectionné');
      return;
    }
    setIsBulkAssignModalOpen(true);
  };

  /**
   * Ouvrir la modal de création de relance en masse (item #16)
   */
  const handleBulkCreateTask = () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun équipage sélectionné');
      return;
    }
    setIsBulkCreateTaskModalOpen(true);
  };

  /**
   * Assigner un utilisateur à tous les équipages sélectionnés
   */
  const handleBulkAssignUser = async (userId: string) => {
    // Utiliser selectedIds (global) au lieu de selectedItems (page courante)
    const crewIds = Array.from(entity.table.selectedIds);

    try {
      // Mise à jour en masse via Supabase
      const { error } = await supabase
        .from('crews')
        .update({ assigned_to: userId })
        .in('id', crewIds);

      if (error) throw error;

      toast.success(`${crewIds.length} équipage(s) assigné(s) avec succès`);

      await refresh();
      await serverPagination.refresh();
    } catch (err) {
      console.error("Erreur lors de l'assignation en masse:", err);
      toast.error("Erreur lors de l'assignation");
      throw err;
    }
  };

  /**
   * Sélectionner tous les résultats correspondant aux filtres actuels (Feature 4.4)
   */
  const handleSelectAllResults = async () => {
    if (!fetchAllFilteredIds) return;

    setIsLoadingAllIds(true);
    try {
      const allIds = await fetchAllFilteredIds({
        search: serverPagination.searchTerm,
        filters: serverPagination.filters,
        filterMode: serverPagination.filterMode,
        // Retour réunion 07/07 (D) : respecter le filtre d'onglet statut
        // (requiredFilters) pour que « tout sélectionner » porte sur le
        // sous-ensemble filtré et non sur tous les équipages.
        requiredFilters: serverPagination.requiredFilters,
      });

      // Mettre à jour selectedIds avec tous les IDs
      entity.table.setSelectedIds(new Set(allIds));
      setIsAllResultsSelected(true);
      toast.success(`${allIds.length} ${CREW_CONFIG.entityNamePlural} sélectionnés`);
    } catch (err) {
      console.error('Erreur sélection globale:', err);
      toast.error('Erreur lors de la sélection');
    } finally {
      setIsLoadingAllIds(false);
    }
  };

  // Reset isAllResultsSelected quand les filtres, la recherche ou l'onglet de
  // statut (requiredFilters) changent. Sans requiredFilters dans les deps, la
  // sélection globale survivait au changement d'onglet → action de masse sur
  // l'ancien périmètre.
  useEffect(() => {
    setIsAllResultsSelected(false);
  }, [serverPagination.filters, serverPagination.searchTerm, serverPagination.requiredFilters]);

  const columns: ColumnDef<Crew>[] = useMemo(() => {
    const cols: ColumnDef<Crew>[] = [
      {
        id: 'select',
        size: 60,
        header: ({ table }) => (
          <CheckboxCell
            row={
              {
                getIsSelected: () => table.getIsAllRowsSelected(),
                toggleSelected: () => table.toggleAllRowsSelected(),
              } as any
            }
          />
        ),
        cell: ({ row }) => <CheckboxCell row={row} />,
      },
      {
        accessorKey: 'crew_number',
        header: '#Équipage',
        cell: ({ row }) => <TableCell>{row.original.crew_number || '-'}</TableCell>,
        enableSorting: true,
        /**
         * Tri numérique custom pour crew_number
         * Résout le problème de tri alphabétique (1, 10, 100, 2 → 1, 2, 10, 100)
         */
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue<string | null>(columnId);
          const b = rowB.getValue<string | null>(columnId);

          // Nulls en dernier
          if (a === null && b === null) return 0;
          if (a === null) return 1;
          if (b === null) return -1;

          // Extraire les nombres et comparer numériquement
          const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
          const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;

          return numA - numB;
        },
      },
      // Retour réunion 07/07 (E) : colonne « N° BPI » (masquée par défaut).
      // Sans ce ColumnDef, la cocher dans le sélecteur n'affichait rien.
      {
        accessorKey: 'bpi_code',
        header: 'N° BPI',
        cell: ({ row }) => <EmptyCell value={row.original.bpi_code} />,
        enableSorting: false,
        size: 140,
      },
      {
        accessorKey: 'name',
        header: "Nom d'équipage",
        cell: ({ row }) => <TableCell>{row.original.name}</TableCell>,
      },
      {
        accessorKey: 'budget',
        header: 'Budget',
        cell: ({ row, getValue }) => {
          const crewId = row.original.id;
          const budget = getValue() as number | null;
          return (
            <div className="px-2 relative">
              <input
                type="text"
                key={`budget-${crewId}-${budget}`}
                defaultValue={budget || ''}
                disabled={!canUpdateEntity('crew')}
                onInput={(e) => {
                  const input = e.currentTarget;
                  input.value = input.value.replace(/[^\d]/g, '');
                }}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== String(budget)) {
                    handleUpdateBudget(crewId, value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className={`w-full h-9 px-3 py-2 pr-8 text-sm bg-white border border-zinc-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 ${!canUpdateEntity('crew') ? 'cursor-not-allowed opacity-60' : ''}`}
                placeholder="0"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-zinc-400 pointer-events-none">
                €
              </span>
            </div>
          );
        },
        size: 150,
        enableSorting: true,
      },
      {
        accessorKey: 'probability',
        header: 'Probabilité',
        cell: ({ row }) => {
          const crewId = row.original.id;

          return (
            <div className="px-2">
              <Select
                variant="inline"
                value={row.original.probability}
                onChange={(e) => handleUpdateProbability(crewId, e.target.value)}
                disabled={!canUpdateEntity('crew')}
                className="w-full"
                style={{
                  color:
                    row.original.probability === 'high'
                      ? '#16a34a'
                      : row.original.probability === 'medium'
                        ? '#f59e0b'
                        : '#6b7280',
                }}
                options={PROBABILITY_SELECT_OPTIONS}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'assigned_to',
        header: 'Assigné à',
        cell: ({ row }) => (
          <EmptyCell
            value={row.original.assigned_user?.full_name || row.original.assigned_user?.email}
          />
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Date de création',
        cell: ({ getValue }) => <DateCell date={getValue() as string} />,
      },
      {
        accessorKey: 'call_count',
        header: "Nb d'appels",
        cell: ({ getValue }) => {
          const count = getValue() as number;
          return <TableCell className="text-zinc-600 text-center">{count || 0}</TableCell>;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ row }) => {
          const status = row.original.status;
          const colors = CREW_STATUS_COLORS[status];
          return (
            <Badge
              className={`flex items-center gap-1 ${colors.bg} ${colors.text} border ${colors.border}`}
            >
              <Tag className="h-3 w-3" />
              {CREW_STATUS_LABELS[status]}
            </Badge>
          );
        },
        size: 180,
      },
      // Colonnes additionnelles (masquées par défaut, disponibles dans le sélecteur)
      {
        accessorKey: 'product',
        header: 'Produit',
        cell: ({ row }) => <EmptyCell value={row.original.product?.name} />,
        size: 150,
      },
      {
        accessorKey: 'vehicle',
        header: 'Véhicule',
        cell: ({ row }) => <EmptyCell value={row.original.vehicle?.registration} />,
        size: 130,
      },
      {
        accessorKey: 'association',
        header: 'Association',
        cell: ({ row }) => <EmptyCell value={row.original.association?.name} />,
        size: 150,
      },
      {
        accessorKey: 'discount',
        header: 'Réduction',
        cell: ({ row }) => <EmptyCell value={row.original.discount?.name} />,
        size: 130,
      },
      {
        accessorKey: 'financing_type',
        header: 'Type de financement',
        cell: ({ row }) => (
          <TableCell>{CREW_FINANCING_TYPE_LABELS[row.original.financing_type] || '-'}</TableCell>
        ),
        size: 160,
      },
      {
        accessorKey: 'report',
        header: 'Motif annulation/report',
        cell: ({ row }) => <EmptyCell value={row.original.report} />,
        size: 200,
      },
      {
        accessorKey: 'has_derogation',
        header: 'Dérogation',
        cell: ({ row }) => <TableCell>{row.original.has_derogation ? 'Oui' : 'Non'}</TableCell>,
        size: 100,
      },
      {
        accessorKey: 'has_vehicle',
        header: 'Possède un véhicule',
        cell: ({ row }) => <TableCell>{row.original.has_vehicle ? 'Oui' : 'Non'}</TableCell>,
        size: 150,
      },
      {
        accessorKey: 'organizes_pvd',
        header: 'Organise PVD',
        cell: ({ row }) => <TableCell>{row.original.organizes_pvd ? 'Oui' : 'Non'}</TableCell>,
        size: 110,
      },
      {
        accessorKey: 'members',
        header: 'Membres',
        cell: ({ row }) => {
          const members = row.original.members;
          if (!members || members.length === 0) return <EmptyCell value={null} />;
          const eventDate = (row.original.product as any)?.start_date ?? null;
          return (
            <div className="px-3 py-2 max-w-64 text-sm space-y-1">
              {members.map((m) => {
                const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
                const over28 = isOver28AtEventDate((m as any).birth_date, eventDate);
                const derogationValidated = (m as any).derogation_validated ?? false;
                return (
                  <div key={m.id}>
                    <div className="truncate">{name}</div>
                    {over28 && !derogationValidated && (
                      <span className="inline-block text-xs font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200 mt-0.5">
                        Dérogation en attente
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        },
        size: 250,
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row }) => (
          <div className="px-3 py-2 max-w-48 truncate text-sm" title={row.original.notes || ''}>
            {row.original.notes || '-'}
          </div>
        ),
        size: 200,
      },
      {
        accessorKey: 'pilot_postal_code',
        header: 'Pilote - Code postal',
        cell: ({ row }) => <EmptyCell value={row.original.pilot_postal_code} />,
        size: 150,
      },
      {
        accessorKey: 'pilot_city',
        header: 'Pilote - Ville',
        cell: ({ row }) => <EmptyCell value={row.original.pilot_city} />,
        size: 150,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionsCell
            onView={() => navigate(ROUTES.DATA.CREWS.DETAIL(row.original.id))}
            onDelete={() => handleDeleteClick(row.original.id, row.original.name ?? undefined)}
            canDelete={canDeleteEntity('crew') && row.original.status === 'archived'}
          />
        ),
        enableSorting: false,
      },
    ];

    return cols;
  }, [
    handleDeleteClick,
    handleUpdateBudget,
    handleUpdateProbability,
    navigate,
    canDeleteEntity,
    canUpdateEntity,
  ]);

  // Colonnes disponibles pour la sélection
  // Utilise generateColumnOptions pour inclure toutes les colonnes de la config
  const columnOptions = useMemo(() => {
    // Générer les options depuis la config (inclut toutes les colonnes sauf hiddenInColumnSelector)
    const baseOptions = generateColumnOptions(CREW_CONFIG, entity.table.columnVisibility);

    return baseOptions;
  }, [entity.table.columnVisibility]);

  // ✅ Ne plus conditionner l'affichage sur loading
  // Le tableau affichera son propre indicateur de chargement

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Équipages' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des équipages"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Équipages' }]} />
      <div className="w-full h-px bg-zinc-200" />

      <PageContent
        title="Équipages"
        description="Gérez les équipages et leur avancement"
        actions={
          <ActionButtons
            leadingControls={
              <SavedSearchToolbar
                entityType="crews"
                currentDefinition={currentSavedSearchDefinition}
                defaultDefinition={defaultSavedSearchDefinition}
                filterConfigs={entity.filterConfigs}
                visibleColumnCount={columnOptions.filter((column) => column.visible).length}
                onApply={applySavedSearchDefinition}
              />
            }
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('crew') ? entity.handleExportCSV : undefined}
            onUpload={canImportData('crew') ? () => entity.setIsImportModalOpen(true) : undefined}
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={canCreateEntity('crew') ? () => entity.setIsCreateModalOpen(true) : undefined}
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <ActiveFilterChips
          filters={entity.table.activeFilters}
          filterConfigs={entity.filterConfigs}
          filterMode={entity.table.filterMode}
          onChange={entity.table.setActiveFilters}
        />

        {/* Bandeau de sélection globale (Feature 4.4) */}
        <SelectAllBanner
          selectedCount={entity.table.selectedCount}
          pageItemCount={crews.length}
          totalFilteredCount={serverPagination.total}
          onSelectAll={handleSelectAllResults}
          onClearSelection={() => {
            entity.table.clearSelection();
            setIsAllResultsSelected(false);
          }}
          entityNamePlural={CREW_CONFIG.entityNamePlural}
          isAllSelected={isAllResultsSelected}
          isLoading={isLoadingAllIds}
        />

        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
          manualPagination={entity.isServerMode}
          loading={entity.loading}
          // ✅ Tri server-side (tri sur l'ensemble du dataset)
          manualSorting={entity.isServerMode}
          onSortChange={serverPagination.setSort}
          activeSortField={serverPagination.sortField}
          activeSortDirection={serverPagination.sortDirection}
          // ✅ Nouveau système de sélection multi-pages
          selectedIds={entity.table.selectedIds}
          onToggleSelection={entity.table.toggleItemSelection}
          onToggleAllPage={entity.table.toggleAllPageSelection}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateCrewModal
          open={entity.isCreateModalOpen}
          onOpenChange={(open) => {
            if (!open) entity.setIsCreateModalOpen(false);
          }}
          onSubmit={async (data: CreateCrewWithBpiData) => {
            // 1. Créer l'équipage (sans le bpi_code qui n'est pas stocké en BDD)
            const { bpi_code: _bpiCode, ...crewDataWithoutBpi } = data.crewData;
            const newCrew = await create(crewDataWithoutBpi);

            // 2. Si des contacts BPI ont été trouvés, les ajouter comme membres avec leurs rôles
            if (newCrew && data.bpiContacts.length > 0) {
              for (const contact of data.bpiContacts) {
                // Passer le rôle (pilot/copilot) déterminé depuis le formulaire BPI
                const success = await addCrewMember(newCrew.id, contact.id, contact.role);
                if (success) {
                  // ✅ Supprimer le prospect correspondant au produit de l'équipage
                  if (newCrew.product_id) {
                    await deleteProspectByContactAndProduct(contact.id, newCrew.product_id);
                  }
                  const roleLabel =
                    contact.role === 'pilot'
                      ? ' (Pilote)'
                      : contact.role === 'copilot'
                        ? ' (Copilote)'
                        : '';
                  toast.success(
                    `${contact.first_name} ${contact.last_name}${roleLabel} ajouté à l'équipage`
                  );
                }
              }
            }

            // 3. Rafraîchir la liste
            await serverPagination.refresh();
          }}
        />
      )}

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: crew, error: supabaseError } = await supabase
            .from('crews')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return crew;
        }}
        onUpdate={async (id, data) => {
          const result = await supabase
            .from('crews')
            .update(data as any)
            .eq('id', id);
          if (result.error) throw result.error;
          return true;
        }}
        existingData={allData}
        entityName={CREW_CONFIG.entityName}
        entityNamePlural={CREW_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${CREW_CONFIG.entityNamePlural}`
            : `Supprimer l'${CREW_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedCount} ${CREW_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer "${entity.deleteConfirm.name}" ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <SendBulkEmailModal
        isOpen={isBulkEmailModalOpen}
        onClose={() => {
          setIsBulkEmailModalOpen(false);
          setSelectedCrewsForEmail([]);
        }}
        entities={selectedCrewsForEmail}
        entityType="crew"
        onSuccess={() => {
          refresh();
          serverPagination.refresh();
          setSelectedCrewsForEmail([]);
        }}
      />

      {/* Modale d'assignation en masse */}
      {isBulkAssignModalOpen && (
        <BulkAssignUserModal
          isOpen={isBulkAssignModalOpen}
          onClose={() => setIsBulkAssignModalOpen(false)}
          count={entity.table.selectedCount}
          entityNamePlural={CREW_CONFIG.entityNamePlural}
          onAssign={handleBulkAssignUser}
        />
      )}

      {/* Modale de création de relance en masse (item #16) */}
      {isBulkCreateTaskModalOpen && (
        <BulkCreateTaskModal
          isOpen={isBulkCreateTaskModalOpen}
          onClose={() => setIsBulkCreateTaskModalOpen(false)}
          entityType="crew"
          entityIds={Array.from(entity.table.selectedIds)}
          entityNamePlural={CREW_CONFIG.entityNamePlural}
          onSuccess={() => {}}
        />
      )}

      {/* Barre d'actions de masse — suppression désactivée pour les crews : elle doit
          passer par la RPC gardée delete_archived_crew (garde statut 'archived' + cascade
          paiements + garde de rôle), que le DELETE en masse direct (removeMany) ne respecte pas. */}
      {entity.table.selectedCount > 0 && (
        <BulkActionsBar
          count={entity.table.selectedCount}
          entityNamePlural={CREW_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onEmail={handleBulkEmail}
          onAssign={handleBulkAssign}
          onCreateTask={handleBulkCreateTask}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={true}
          showAssignAction={canManageAssignments()}
          showCreateTaskAction={true}
          canDelete={false}
          canExport={canExportData('crew')}
        />
      )}
    </PageLayout>
  );
}
